import React from "react";
import { customSanitize } from "../../../utils/customSanitize";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { InfoBoxElementType, TextInfoBoxType } from "../InfoBoxesModule";
import { InfoboxesOptionsType } from "@/types/ModuleOptionsType";
import styles from "./InfoBox.module.scss";
import { getItemAttributes } from "@/components/_primitives/utils/getItemAttributes";
import { Breakpoint } from "@aleksundshantu/aunds-utils-react/layout";
import { Button } from "@/components/Button/Button";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { MediaItemResponsive } from "@/components/MediaItem/MediaItemResponsive";
import {
  getBoxPadding,
  replaceExtendedLinkText,
  checkLinkAvailable,
  getShowBoxClasses,
} from "@/utils/infoBoxUtils";
import { IconGridModuleType } from "@/modules/IconGridModule/IconGridModule";

export type InfoBoxLayoutType = "v1" | "v2";

type InfoBoxPropsBase = {
  extraClass: string;
  infoBoxOptions: InfoboxesOptionsType;
  itemWidth?: Record<Breakpoint, string>;
  show_cardbox: boolean;
  show_divider: boolean;
  heading_level: number;
  layout?: InfoBoxLayoutType;
};

type InfoBoxProps = InfoBoxPropsBase &
  (
    | {
        type: "iconInfobox";
        iconInfobox: Exclude<
          IconGridModuleType["icon_grid"]["elements"],
          false
        >[number];
      }
    | {
        type: "textInfobox";
        textInfobox: TextInfoBoxType;
      }
    | {
        type: "infobox";
        infobox: InfoBoxElementType;
      }
  );

export const InfoBox = (props: InfoBoxProps) => {
  const {
    type,
    extraClass,
    infoBoxOptions,
    itemWidth,
    show_cardbox,
    show_divider,
    heading_level,
    layout = "v1",
  } = props;

  //In Backend we can get both true at the same time, so show_cardbox has priority
  const effectiveShowCardbox = show_cardbox;
  const effectiveShowDivider = show_cardbox ? false : show_divider;

  const { show_extended_link, button_type, box } = infoBoxOptions ?? {};
  let element;
  switch (type) {
    case "iconInfobox":
      element = props.iconInfobox;
      break;
    case "infobox":
      element = props.infobox;
      break;
    case "textInfobox":
      element = props.textInfobox;
      break;
  }

  const title =
    "infobox_title" in element ? element.infobox_title : element.title;
  const link = "link" in element ? element.link : undefined;
  const link_extended =
    "link_extended" in element ? element.link_extended : undefined;

  const {
    // show_card_box: show_box,
    show_border_radius,
    show_box_shadow,
    box_padding,
  } = box;

  const showBowClasses = getShowBoxClasses(
    effectiveShowCardbox,
    effectiveShowDivider,
    show_border_radius,
    show_box_shadow,
  );

  const isCentered = extraClass?.includes("has-text-centered");
  const attr = getItemAttributes({
    itemWidth: itemWidth,
    className: `${showBowClasses} column is-12 is-5-desktop ${styles.infobox} ${
      !effectiveShowCardbox && effectiveShowDivider ? styles["has-divider"] : ""
    } ${type === "textInfobox" ? styles["is-text-infobox"] : ""}`,
    itemAttrFor: "infobox",
  });

  const TITLE_JSX = title &&
    (button_type !== "link-in-title" ||
      !checkLinkAvailable(
        (show_extended_link ? link_extended : link) || false,
        show_extended_link,
      )) && (
      <span
        role="heading"
        aria-level={heading_level}
        className={`infobox-title is-block  ${layout === "v1" ? "p-0" : ""}`}
        dangerouslySetInnerHTML={{ __html: customSanitize(title) }}
      />
    );

  return (
    <article
      {...attr}
      style={{
        ...attr.style,
        ...(getBoxPadding(box_padding) as React.CSSProperties),
      }}
    >
      {type === "textInfobox" && "infobox_image" in element && (
        <MediaItemResponsive
          className={`${styles["image-wrapper"]}`}
          image_desktop={element.infobox_image}
          sizes="(max-width: 767px) 40vw, (max-width: 1023px) 80vw, (max-width: 1407px) 50vw, 768px"
        />
      )}

      {layout === "v1" && <>{TITLE_JSX}</>}

      {layout === "v2" && (
        <>
          {TITLE_JSX}

          {title &&
            !show_extended_link &&
            link &&
            link.url &&
            button_type === "link-in-title" && (
              <Button
                role="button"
                className={`mb-0 ${isCentered ? "mx-auto" : ""}`}
                type={"secondary"}
                href={link.url}
                target={link.target === "" ? "_self" : link.target}
                aria-label={link.aria_label}
              >
                {link.title}
              </Button>
            )}

          {title &&
            button_type === "link-in-title" &&
            show_extended_link &&
            link_extended && (
              <ExtendedLink
                extendedLink={replaceExtendedLinkText(title, link_extended)}
                btnType={"secondary"}
                extraClass={`${isCentered ? "mx-auto" : ""} mt-0 infobox-title`}
              />
            )}

          <ContentTag
            extraClass={`infobox-text`}
            content={
              type === "iconInfobox"
                ? "content" in element
                  ? element.content
                  : ""
                : "infobox" in element
                  ? (element.infobox ?? "")
                  : ""
            }
          />

          {/* TODO: implement link-in-tile */}
          {!show_extended_link &&
            link &&
            link.url &&
            button_type !== "link-in-title" && (
              <Button
                role="button"
                className={`mb-0 ${isCentered ? "mx-auto" : ""}`}
                type={button_type}
                href={link.url}
                target={link.target === "" ? "_self" : link.target}
                aria-label={link.aria_label}
              >
                {link.title}
              </Button>
            )}

          {show_extended_link &&
            link_extended &&
            button_type !== "link-in-title" && (
              <ExtendedLink
                extendedLink={link_extended}
                btnType={button_type}
                extraClass={`mt-5 ${isCentered ? "mx-auto" : ""}`}
              />
            )}
        </>
      )}
    </article>
  );
};
